import {
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { Button } from '../../../../design-system/button/button';
import { DataTableNew } from '../../../../design-system/data-table-new';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { PayrollView } from '../../../../types/entity-types';
import { payrollTableColumnsDefinition } from './payroll-table-column-definition';

const STATUS_COLUMN_ID = 'status';
const PAGE_SIZE = 20;

type PayrollTableProps = {
  payrolls: PayrollView[];
  handleNewPayroll: () => void;
  onSelectPayroll?: (payroll: PayrollView) => void;
};

export const PayrollTable = ({ payrolls, handleNewPayroll, onSelectPayroll }: PayrollTableProps) => {
  const handleRowSelect = (id: string) => {
    if (onSelectPayroll) {
      const payroll = payrolls.find((p) => p.id === id);
      if (payroll) {
        onSelectPayroll(payroll);
      }
    }
  };

  const table = useReactTable({
    data: payrolls,
    columns: payrollTableColumnsDefinition,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    initialState: {
      sorting: [{ id: STATUS_COLUMN_ID, desc: false }],
      pagination: {
        pageSize: PAGE_SIZE,
      },
    },
  });

  const statusColumn = table.getColumn(STATUS_COLUMN_ID);

  return (
    <Stack space={4} align="stretch">
      <Inline space={4} justify="end">
        {statusColumn && <DataTableNew.ColumnFilter column={statusColumn} label="Status" searchable={false} />}
        <DataTableNew.GlobalSearch table={table} placeholder="Search payrolls" />
        <Button variant="default" iconLeft={<Plus />} onClick={handleNewPayroll}>
          New Payroll
        </Button>
      </Inline>
      <DataTableNew.Table
        table={table}
        onSelectRow={handleRowSelect}
        noResultsMessage="No payrolls found"
        aria-label="Payrolls table"
      />
      {table.getPageCount() > 1 && (
        <Inline justify="end">
          <DataTableNew.Pagination table={table} />
        </Inline>
      )}
    </Stack>
  );
};
